import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiClientError } from '../../api/http'
import {
  cancelNoonSalesImport,
  getLatestNoonSalesImportJob,
  getNoonSalesImportJob,
  listNoonSalesImportRows,
  uploadNoonSalesImport,
} from './noonSalesImportApi'
import type { NoonSalesImportJob, NoonSalesImportRowPage } from './types'

const ACTIVE_STATUSES = new Set(['QUEUED', 'RUNNING'])
const ROW_PAGE_SIZE = 20
const POLL_DELAY_MS = 2_000
const MAX_POLL_DELAY_MS = 30_000

interface Scoped<T> {
  scope: string
  value: T
}

interface ScopedRowPage extends Scoped<NoonSalesImportRowPage | null> {
  jobId: number | null
  page: number
}

export function isNoonSalesImportActive(job: NoonSalesImportJob): boolean {
  return ACTIVE_STATUSES.has(job.status)
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiClientError
    && (error.status === 404 || error.code === 'NOON_SALES_IMPORT_JOB_NOT_FOUND')
}

function isFinalPollFailure(error: unknown): boolean {
  return error instanceof ApiClientError
    && (error.status === 401 || error.status === 403 || isNotFound(error))
}

function isTransientPollFailure(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return true
  return error.status === 408 || error.status === 429 || error.status >= 500
}

export interface UseNoonSalesImportJobResult {
  job: NoonSalesImportJob | null
  rowPage: NoonSalesImportRowPage | null
  page: number
  loadingLatest: boolean
  submitting: boolean
  cancelling: boolean
  error: unknown
  pollWarning: unknown
  submit: (file: File, sourceTimeZone?: string, partnerId?: string) => Promise<NoonSalesImportJob | undefined>
  cancel: () => Promise<void>
  setPage: (page: number | ((current: number) => number)) => void
  reloadLatest: () => void
}

export function useNoonSalesImportJob(accessToken: string, connectionId: number): UseNoonSalesImportJobResult {
  const scope = useMemo(() => `${accessToken}\u0000${connectionId}`, [accessToken, connectionId])
  const [jobState, setJobState] = useState<Scoped<NoonSalesImportJob | null>>({ scope, value: null })
  const [rowState, setRowState] = useState<ScopedRowPage>({ scope, jobId: null, page: 0, value: null })
  const [pageState, setPageState] = useState<Scoped<number>>({ scope, value: 0 })
  const [loadingState, setLoadingState] = useState<Scoped<boolean>>({ scope, value: true })
  const [submittingState, setSubmittingState] = useState<Scoped<boolean>>({ scope, value: false })
  const [cancellingState, setCancellingState] = useState<Scoped<boolean>>({ scope, value: false })
  const [errorState, setErrorState] = useState<Scoped<unknown>>({ scope, value: null })
  const [warningState, setWarningState] = useState<Scoped<unknown>>({ scope, value: null })
  const [latestRequest, setLatestRequest] = useState(0)
  const [pollRestart, setPollRestart] = useState(0)
  const scopeGeneration = useRef(0)
  const latestGeneration = useRef(0)
  const pollGeneration = useRef(0)
  const rowGeneration = useRef(0)
  const operationGeneration = useRef(0)
  const pollTimer = useRef<number | null>(null)
  const currentJobId = useRef<number | null>(null)

  const job = jobState.scope === scope ? jobState.value : null
  const page = pageState.scope === scope ? pageState.value : 0
  const jobId = job?.id ?? null
  const canLoadRows = job !== null
    && (job.processedRowCount > 0 || !isNoonSalesImportActive(job))
  const rowPage = rowState.scope === scope && rowState.jobId === jobId && rowState.page === page
    ? rowState.value : null

  const clearPoll = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current)
      pollTimer.current = null
    }
  }, [])

  useEffect(() => {
    scopeGeneration.current += 1
    latestGeneration.current += 1
    pollGeneration.current += 1
    rowGeneration.current += 1
    operationGeneration.current += 1
    clearPoll()
    return () => {
      scopeGeneration.current += 1
      latestGeneration.current += 1
      pollGeneration.current += 1
      rowGeneration.current += 1
      operationGeneration.current += 1
      clearPoll()
    }
  }, [clearPoll, scope])

  useEffect(() => {
    currentJobId.current = jobId
  }, [jobId])

  useEffect(() => {
    const generation = ++latestGeneration.current
    const scopeAtStart = scopeGeneration.current
    void getLatestNoonSalesImportJob(accessToken, connectionId)
      .then(nextJob => {
        if (scopeGeneration.current !== scopeAtStart || latestGeneration.current !== generation) return
        if (currentJobId.current !== nextJob.id) {
          rowGeneration.current += 1
          setRowState({ scope, jobId: nextJob.id, page: 0, value: null })
          setPageState({ scope, value: 0 })
        }
        setJobState({ scope, value: nextJob })
      })
      .catch(caught => {
        if (scopeGeneration.current !== scopeAtStart || latestGeneration.current !== generation) return
        if (!isNotFound(caught)) setErrorState({ scope, value: caught })
      })
      .finally(() => {
        if (scopeGeneration.current === scopeAtStart && latestGeneration.current === generation) {
          setLoadingState({ scope, value: false })
        }
      })
  }, [accessToken, connectionId, latestRequest, scope])

  useEffect(() => {
    if (!job || !isNoonSalesImportActive(job)) return
    const jobId = job.id
    const generation = ++pollGeneration.current
    const scopeAtStart = scopeGeneration.current
    let retryDelay = POLL_DELAY_MS
    let stopped = false

    const current = () => !stopped
      && scopeGeneration.current === scopeAtStart
      && pollGeneration.current === generation

    const schedule = (delay: number) => {
      clearPoll()
      pollTimer.current = window.setTimeout(run, delay)
    }

    const run = () => {
      pollTimer.current = null
      void getNoonSalesImportJob(accessToken, jobId)
        .then(nextJob => {
          if (!current() || nextJob.id !== jobId) return
          retryDelay = POLL_DELAY_MS
          setWarningState({ scope, value: null })
          setJobState({ scope, value: nextJob })
          if (isNoonSalesImportActive(nextJob)) schedule(POLL_DELAY_MS)
        })
        .catch(caught => {
          if (!current()) return
          if (isFinalPollFailure(caught) || !isTransientPollFailure(caught)) {
            setErrorState({ scope, value: caught })
            return
          }
          setWarningState({ scope, value: caught })
          schedule(retryDelay)
          retryDelay = Math.min(retryDelay * 2, MAX_POLL_DELAY_MS)
        })
    }

    schedule(POLL_DELAY_MS)
    return () => {
      stopped = true
      pollGeneration.current += 1
      clearPoll()
    }
  }, [accessToken, clearPoll, job, pollRestart, scope])

  useEffect(() => {
    if (jobId === null || !canLoadRows) return
    const requestedJobId = jobId
    const requestedPage = page
    const generation = ++rowGeneration.current
    const scopeAtStart = scopeGeneration.current
    void listNoonSalesImportRows(accessToken, requestedJobId, requestedPage, ROW_PAGE_SIZE)
      .then(nextPage => {
        if (scopeGeneration.current === scopeAtStart && rowGeneration.current === generation) {
          setRowState({ scope, jobId: requestedJobId, page: requestedPage, value: nextPage })
        }
      })
      .catch(caught => {
        if (scopeGeneration.current === scopeAtStart && rowGeneration.current === generation) {
          setErrorState({ scope, value: caught })
        }
      })
    return () => {
      rowGeneration.current += 1
    }
  }, [accessToken, canLoadRows, jobId, page, scope])

  const submit = useCallback(async (file: File, sourceTimeZone?: string, partnerId?: string) => {
    const generation = ++operationGeneration.current
    const scopeAtStart = scopeGeneration.current
    latestGeneration.current += 1
    pollGeneration.current += 1
    rowGeneration.current += 1
    clearPoll()
    setJobState({ scope, value: null })
    setRowState({ scope, jobId: null, page: 0, value: null })
    setPageState({ scope, value: 0 })
    setErrorState({ scope, value: null })
    setWarningState({ scope, value: null })
    setSubmittingState({ scope, value: true })
    try {
      const nextJob = await uploadNoonSalesImport(accessToken, connectionId, file, sourceTimeZone, partnerId)
      if (scopeGeneration.current === scopeAtStart && operationGeneration.current === generation) {
        setJobState({ scope, value: nextJob })
        return nextJob
      }
    } catch (caught) {
      if (scopeGeneration.current === scopeAtStart && operationGeneration.current === generation) {
        setErrorState({ scope, value: caught })
      }
    } finally {
      if (scopeGeneration.current === scopeAtStart && operationGeneration.current === generation) {
        setSubmittingState({ scope, value: false })
      }
    }
  }, [accessToken, clearPoll, connectionId, scope])

  const cancel = useCallback(async () => {
    if (!job || !isNoonSalesImportActive(job)) return
    const jobId = job.id
    const generation = ++operationGeneration.current
    const scopeAtStart = scopeGeneration.current
    pollGeneration.current += 1
    clearPoll()
    setErrorState({ scope, value: null })
    setCancellingState({ scope, value: true })
    let cancelled = false
    try {
      const nextJob = await cancelNoonSalesImport(accessToken, jobId)
      if (scopeGeneration.current === scopeAtStart && operationGeneration.current === generation) {
        cancelled = true
        setJobState({ scope, value: nextJob })
      }
    } catch (caught) {
      if (scopeGeneration.current === scopeAtStart && operationGeneration.current === generation) {
        setErrorState({ scope, value: caught })
      }
    } finally {
      if (scopeGeneration.current === scopeAtStart && operationGeneration.current === generation) {
        setCancellingState({ scope, value: false })
        if (!cancelled) setPollRestart(current => current + 1)
      }
    }
  }, [accessToken, clearPoll, job, scope])

  const setPage = useCallback((next: number | ((current: number) => number)) => {
    setPageState(current => {
      const value = current.scope === scope ? current.value : 0
      return { scope, value: typeof next === 'function' ? next(value) : next }
    })
  }, [scope])

  const reloadLatest = useCallback(() => {
    latestGeneration.current += 1
    setLoadingState({ scope, value: true })
    setErrorState({ scope, value: null })
    setLatestRequest(current => current + 1)
  }, [scope])

  return {
    job,
    rowPage,
    page,
    loadingLatest: loadingState.scope === scope ? loadingState.value : true,
    submitting: submittingState.scope === scope ? submittingState.value : false,
    cancelling: cancellingState.scope === scope ? cancellingState.value : false,
    error: errorState.scope === scope ? errorState.value : null,
    pollWarning: warningState.scope === scope ? warningState.value : null,
    submit,
    cancel,
    setPage,
    reloadLatest,
  }
}
